import type { LessonError } from '@/lib/api-client';

/**
 * Map a backend `LessonError.code` to a short, human-friendly title for
 * panel failure cards. Unknown codes fall back to a generic title - the
 * backend's `error.message` still surfaces in the body either way.
 */
const TITLE_BY_CODE: Record<string, string> = {
  OVERVIEW_GENERATION_FAILED: "Couldn't generate this lesson",
  RATE_LIMITED: 'Our AI is busy',
  TIMEOUT: 'This took too long',
  MODEL_ERROR: 'Something went wrong',
  SOURCE_UNREADABLE: "Source couldn't be read",
  CONTEXT_GENERATION_FAILED: "Couldn't build your lesson",
  ALL_SOURCES_FAILED: "Couldn't read your sources",
  QUEUE_STUCK: 'This took too long',
  UPSTREAM_ERROR: 'Something went wrong',
  BAD_INPUT: 'Check your request',
  GUARDRAIL_REJECTED: "This topic isn't allowed",
};

// Friendly, user-facing names for the internal pipeline steps. Lets failure
// copy say "Couldn't generate the quiz" instead of leaking a raw step key
// like "byte_selection" or the word "step" at the user.
const LABEL_BY_STEP: Record<string, string> = {
  overview: 'lesson',
  byte_selection: 'lesson',
  text: 'lesson',
  mindmap: 'mind map',
  video: 'video',
  podcast: 'podcast',
  quiz: 'quiz',
  flashcards: 'flashcards',
  glossary: 'glossary',
  highlights: 'highlights',
};

/**
 * Short, user-facing body copy per code. Deliberately plain and actionable -
 * no provider names, HTTP codes, or internal jargon (those live in the raw
 * `error.message`, exposed separately as `technical` for debugging). Unknown
 * codes fall back to `GENERIC_BODY`.
 */
const BODY_BY_CODE: Record<string, string> = {
  OVERVIEW_GENERATION_FAILED:
    'Please try again, or adjust your prompt if it keeps failing.',
  RATE_LIMITED:
    'Our AI is handling a lot of requests right now. Try again in a moment.',
  TIMEOUT: 'Generation took too long. Please try again.',
  MODEL_ERROR: 'The AI service hit a snag. Please try again shortly.',
  UPSTREAM_ERROR: 'The AI service hit a snag. Please try again shortly.',
  SOURCE_UNREADABLE:
    "We couldn't read one of your sources. Check the file and try again.",
  CONTEXT_GENERATION_FAILED:
    'Something went wrong while building your lesson. Please try again.',
  ALL_SOURCES_FAILED:
    "We couldn't process any of your sources. Please try again.",
  QUEUE_STUCK: "Generation didn't finish in time. Please try again.",
  BAD_INPUT:
    "Something in this request couldn't be processed. Edit it and try again.",
  GUARDRAIL_REJECTED:
    "We can't create a lesson on this. Try rephrasing or a different topic.",
};

const GENERIC_BODY = 'Something went wrong. Please try again.';

/**
 * Codes that won't succeed on a simple retry - the user needs to take an
 * action (re-upload, edit prompt, etc.). UI swaps retry copy when one of
 * these is present.
 */
const USER_ACTION_CODES = new Set<string>([
  'SOURCE_UNREADABLE',
  'BAD_INPUT',
  // A safety-filter rejection won't clear on a plain retry - the user has to
  // change the prompt, so the failure card offers Edit instead of Retry.
  'GUARDRAIL_REJECTED',
]);

export interface LessonFailureUi {
  title: string;
  body: string;
  needsUserAction: boolean;
  code?: string;
  timestamp?: string;
  /** Raw backend `error.message` - for debugging only (logs / tooltip), never the headline. */
  technical?: string;
}

/**
 * Build the display copy for a failure card from a `LessonError`. Falls
 * back to step-aware generic copy when error is missing (B7 in the
 * scenario catalog).
 */
export function describeFailure(
  error: LessonError | null | undefined,
  step?: string
): LessonFailureUi {
  if (!error) {
    return {
      title: "Couldn't load this content",
      body: 'We hit a snag. Try again, or reload the page.',
      needsUserAction: false,
    };
  }
  const mappedTitle = error.code ? TITLE_BY_CODE[error.code] : undefined;
  const mappedBody = error.code ? BODY_BY_CODE[error.code] : undefined;
  // Fallback title for unmapped codes - reference the feature by its
  // friendly name where we know it, never the raw step key or "step".
  const stepLabel = step ? LABEL_BY_STEP[step] : undefined;
  const fallbackTitle = stepLabel
    ? `Couldn't generate the ${stepLabel}`
    : 'Something went wrong';
  return {
    title: mappedTitle ?? fallbackTitle,
    // Friendly, code-mapped copy - NOT the raw `error.message`, which leaks
    // provider/HTTP details. The raw text rides along as `technical`.
    body: mappedBody ?? GENERIC_BODY,
    needsUserAction: error.code ? USER_ACTION_CODES.has(error.code) : false,
    code: error.code,
    timestamp: error.timestamp,
    technical: error.message || undefined,
  };
}

/**
 * The notice for flow steps a lesson (or new version) was created without,
 * because the user had reached that step's limit or it isn't on their plan
 * (`skipped_steps` on the generate / regenerate response). Null when nothing
 * was skipped. The steps stay available as "Generate" in the lesson.
 */
export function skippedStepsNotice(
  steps: readonly string[] | undefined
): string | null {
  const labels = [...new Set((steps ?? []).map(s => LABEL_BY_STEP[s] ?? s))];
  if (labels.length === 0) return null;
  const list =
    labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  return `Created without the ${list}: you've reached your plan's limit for now. You can generate ${labels.length === 1 ? 'it' : 'them'} from the lesson later.`;
}
